/**
 * Onboarding Step 2 — Website install.
 *
 * 2026-05-22 redesign:
 *   - Wraps with OnboardingShell (stepper + outcome rail)
 *   - Replaces SkipForNowDialog with inline "I'll install later" checkbox
 *   - [Back] [Continue] bottom row; Continue enabled when snippet verified
 *     OR checkbox checked
 *   - Pulse copy: "Listening for your snippet…"
 *   - Uses shared SnippetCard component for the install code
 *
 * Verification: reads me.snippet_verified_at (added 2026-05-22 to /me).
 * Polling: every 1.5s while next_step is still "website", to catch the
 * verified flag flipping when the snippet fires on the user's site.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ExternalLink, Loader2, AlertCircle } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { computeRailState } from '@/lib/onboardingState';
import { buildSnippet } from '@/lib/snippet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { WhatsAppGlyphButton } from '@/components/WhatsAppGlyphButton';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { SnippetCard } from '@/components/SnippetCard';
import { InstallGuide } from '@/components/install/InstallGuide';
import { GtmInstallCard } from '@/components/install/GtmInstallCard';
import type { SiteTechStack } from '@/types/auth';

const POLL_INTERVAL_MS = 1_500;
const SCAN_TIMEOUT_MS = 15_000;

const STACK_LABELS: Record<SiteTechStack, string> = {
  gtm: 'Google Tag Manager',
  gtag: 'Google tag (gtag.js)',
  wordpress: 'WordPress',
  shopify: 'Shopify',
  wix: 'Wix',
  squarespace: 'Squarespace',
  webflow: 'Webflow',
  framer: 'Framer',
  bigcommerce: 'BigCommerce',
  astro: 'Astro',
  raw_html: 'raw HTML',
  unknown: 'unknown',
};

function DetectionBanner({
  stack,
  scannedAt,
}: {
  stack: SiteTechStack | null;
  scannedAt: string | null;
}) {
  const [scanTimedOut, setScanTimedOut] = useState(false);

  useEffect(() => {
    if (scannedAt) return;
    const timer = setTimeout(() => setScanTimedOut(true), SCAN_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [scannedAt]);

  if (!scannedAt && !scanTimedOut) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-info/20 bg-info/5 p-4 text-sm">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-info" />
        <span className="text-muted-foreground">Scanning your site…</span>
      </div>
    );
  }

  if (!scannedAt || stack === 'raw_html' || stack === 'unknown' || stack === null) {
    return (
      <div className="flex items-start gap-3 rounded-2xl border bg-muted/40 p-4 text-sm">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-muted-foreground">
          We couldn't detect your site's builder. Follow the generic steps below.
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-info/20 bg-info/5 p-4 text-sm">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-info" />
      <span className="text-dark">
        We detected <strong>{STACK_LABELS[stack]}</strong> on your site. Use the steps below.
      </span>
    </div>
  );
}

export function OnboardingWebsitePage() {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [willSkip, setWillSkip] = useState(false);
  const [addWidget, setAddWidget] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const clientId = me?.memberships[0]?.client_id ?? '';
  const websiteUrl = me?.website_url ?? '';
  const verified = !!me?.snippet_verified_at;

  // Bounce direct-URL arrivals who never had next_step='website'.
  // System admins can preview the page for QA — they have next_step=null
  // by design and would otherwise always be bounced to dashboard.
  useEffect(() => {
    if (!me) return;
    if (me.is_system_admin) return;
    if (verified) return; // post-verify, stay on page so Continue is visible
    if (me.next_step === 'business') navigate('/onboarding', { replace: true });
    else if (me.next_step !== 'website' && me.next_step !== null) {
      // Already past website (e.g., on connect or beyond) — let them stay
      // and Continue when they want. No redirect.
    }
  }, [me, navigate, verified]);

  // Poll /me every 1.5s while waiting for the snippet to fire. Express the
  // condition as "still waiting" (not verified yet AND user hasn't decided
  // to skip) instead of "we're on the website step". The previous condition
  // (isWebsiteStep) broke for system admins, whose next_step is forced to
  // null so they never bounce into onboarding — but they still need /me to
  // refresh on this page so the pulse can flip to "Install confirmed".
  useEffect(() => {
    if (verified || willSkip) return;
    const id = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [verified, willSkip, queryClient]);

  const canContinue = verified || willSkip;

  async function handleContinue() {
    if (!canContinue || submitting) return;
    setSubmitting(true);
    try {
      if (!verified && willSkip) {
        // Commit the skip on the backend before advancing.
        await apiRequest('/onboarding/skip', {
          method: 'POST',
          body: { step: 'snippet' },
        });
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      } else if (clientId) {
        // Installing — persist the widget choice. Best-effort: never trap the
        // user on this step over it. Dormant until the number lands at step 4.
        try {
          await apiRequest(`/clients/${clientId}/widget-config`, {
            method: 'PATCH',
            body: { enabled: addWidget },
          });
        } catch {
          /* swallow — non-blocking */
        }
      }
      navigate('/');
    } finally {
      setSubmitting(false);
    }
  }

  const snippet = clientId ? buildSnippet(clientId, me?.site_tech_stack ?? null) : '';

  return (
    <OnboardingShell
      current={2}
      title="Install HeyMotek on your site"
      subtitle="One line of code. We'll detect your platform and walk you through it."
      state={computeRailState(me ?? null)}
    >
      <div className="space-y-5">
        <DetectionBanner
          stack={me?.site_tech_stack ?? null}
          scannedAt={me?.site_tech_scanned_at ?? null}
        />

        {/* GTM auto-install — only when GTM detected. Manual snippet flow
            below remains as fallback (per PLAN_GTM_AUTO_INSTALL.md). */}
        {me?.site_tech_stack === 'gtm' && (
          <GtmInstallCard
            websiteUrl={me?.website_url ?? null}
            snippetVerified={verified}
          />
        )}

        {/* Clear visual cut between the auto (GTM) path and the manual path. */}
        {me?.site_tech_stack === 'gtm' && (
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-dark/10" />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              or
            </span>
            <span className="h-px flex-1 bg-dark/10" />
          </div>
        )}

        {/* Instructions card. When GTM is detected, the auto-install card above
            is the primary path; this is the manual alternative. */}
        <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
          <h2 className="text-xl font-bold text-dark">
            {me?.site_tech_stack === 'gtm' ? 'Install manually' : 'Add the snippet'}
          </h2>
          <div className="mt-4 text-sm text-dark">
            <InstallGuide stack={me?.site_tech_stack ?? 'unknown'} />
          </div>
        </div>

        {/* The snippet — shared SnippetCard */}
        {clientId && (
          <SnippetCard label="The snippet" snippet={snippet} mode="copy-button" />
        )}

        {/* Test URL card — hidden once the snippet has verified. */}
        {websiteUrl && !verified && (
          <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
            <h2 className="text-xl font-bold text-dark">After pasting, test it on your site</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Open your site in a new tab. We'll detect the snippet automatically as soon as a visit comes in.
            </p>
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-dark px-6 py-3 font-bold text-white hover:bg-dark/90"
            >
              Open {websiteUrl.replace(/^https?:\/\//, '')}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}

        {/* Status pulse — its own row */}
        <div className="flex items-center gap-3 rounded-2xl bg-card shadow-card p-4">
          {verified ? (
            <>
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <span className="text-sm text-dark">
                <strong>Install confirmed.</strong> Your snippet is live. <span className="text-emerald-600">✓</span>
              </span>
            </>
          ) : (
            <>
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand" />
              </span>
              <span className="text-sm text-muted-foreground">Listening for your snippet…</span>
            </>
          )}
        </div>

        {/* WhatsApp button (widget) opt-in — its own section, shown after the
            snippet verifies. Persisted on Continue; dormant until the WhatsApp
            number lands at step 4, then auto-activates. The real WhatsApp logo
            sits bottom-right (where the floating button lands) so it's clear
            what's being added. */}
        {clientId && verified && (
          <div>
            <h2 className="mb-3 px-1 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              WhatsApp button
            </h2>
            <label className="relative flex cursor-pointer items-start gap-3 overflow-hidden rounded-3xl bg-card p-5 shadow-card sm:p-6">
              <Checkbox
                checked={addWidget}
                onCheckedChange={(c) => setAddWidget(c === true)}
                className="mt-0.5"
              />
              <span className="flex-1 pr-20">
                <span className="flex flex-wrap items-center gap-2 text-base font-bold text-dark">
                  Add the HeyMotek WhatsApp button
                  <span className="rounded-full bg-info/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-info">
                    Recommended
                  </span>
                </span>
                <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                  We'll render a floating WhatsApp button on your site so every customer
                  click is captured. It goes live once you connect WhatsApp in the last
                  step. You can customize it or turn it off anytime in Integrations.
                </span>
              </span>
              <WhatsAppGlyphButton className="pointer-events-none absolute bottom-4 right-4 h-10 w-10 shadow-md" />
            </label>
          </div>
        )}

        {/* Skip-later — its own row */}
        {!verified && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Checkbox
              checked={willSkip}
              onCheckedChange={(c) => setWillSkip(c === true)}
            />
            <span>I'll install later</span>
          </label>
        )}

        {/* Back + Continue. Back to Step 1 is supported because Step 1 is
            now revisit-aware (fetches existing data + PATCHes on resubmit). */}
        <div className="pt-2 flex gap-3">
          <a
            href="/onboarding"
            className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-dark/10 px-5 py-3.5 text-base font-bold text-muted-foreground hover:bg-bg-tint hover:text-dark transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </a>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue || submitting}
            className={
              canContinue
                ? 'flex-1 rounded-full bg-brand text-white hover:bg-brand-dark py-3.5 text-base font-bold'
                : 'flex-1 rounded-full bg-brand/40 text-white py-3.5 text-base font-bold cursor-not-allowed'
            }
          >
            {submitting ? 'saving…' : 'Continue'}
          </Button>
        </div>
      </div>
    </OnboardingShell>
  );
}
